import { useQuery } from '@tanstack/react-query';
import { env } from '@/config/env';
import { buildReportsCatalog } from '../catalog';
import { reportKeys } from '../reports.keys';
import {
  PowerBiInteractionRequiredError,
  reportsService,
} from '../reportsService';
import type { ReportCatalogItem } from '../types';

/**
 * The Reports catalog: every report in the configured Power BI workspace
 * (`VITE_POWERBI_GROUP_ID`), as nav items + `/reports/:slug` pages. Fetched
 * once per session and shared by the sidebar, the /reports redirect and the
 * report page, so publishing a report to the workspace is all it takes for
 * it to appear — no config change, no deploy.
 *
 * Unconfigured, the query stays idle and `data` is undefined; callers treat
 * that as "no reports". Signed-out or without Power BI access it errors, and
 * the sidebar simply shows no sub-items.
 */
export function useReportsCatalog() {
  const groupId = env.VITE_POWERBI_GROUP_ID ?? '';

  return useQuery<ReportCatalogItem[], Error>({
    queryKey: reportKeys.list(groupId),
    queryFn: async () => {
      const reports = await reportsService.listWorkspaceReports(groupId);
      return buildReportsCatalog(reports, groupId);
    },
    enabled: !!groupId,
    // The listing changes only when someone publishes to the workspace; a
    // reload picks that up. Within a session, don't refetch on every route.
    staleTime: 10 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    // A consent prompt won't clear itself — retrying just delays the message.
    retry: (failureCount, error) =>
      !(error instanceof PowerBiInteractionRequiredError) && failureCount < 1,
    throwOnError: false,
  });
}
